import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { GeoBlocker } from "@/components/GeoBlocker";
import { GeoProvider } from "@/context/GeoContext";
import { GeoAwareAnalytics } from "@/components/GeoAwareAnalytics";

import { GeoAwareSalespanel } from "@/components/GeoAwareSalespanel";
import { LuxurySessionTracker } from "@/components/analytics/LuxurySessionTracker";
// Framer Motion removed from page transitions for performance - using CSS animations instead
import { lazy, Suspense, useEffect, useState, useCallback, useRef } from "react";
import { cn } from "@/lib/utils";
import { reportCoreWebVitals, initWebVitalsReporting } from "./utils/performance";
import { hapticFeedback } from "./hooks/useHapticFeedback";
import { pingCurrentPage } from "./utils/indexNowPing";
import { ErrorBoundary } from "@/components/ErrorBoundary";

// Critical path: Only HomePage loaded synchronously (LCP optimization)
import HomePage from "./pages/HomePage";
// ValuationPage lazy-loaded - most users land on homepage first
const ValuationPage = lazy(() => import("./pages/ValuationPage"));
import Header from "./components/navigation/Header";
import Footer from "./components/navigation/Footer";
import ScrollToTop from "./components/ScrollToTop";
import PerformanceOptimizer from "./components/PerformanceOptimizer";
import BreadcrumbSchema from "./components/BreadcrumbSchema";
import MobileOptimizer from "./components/MobileOptimizer";
import GlobalSchema from "./components/GlobalSchema";

import { MobileBottomNav } from "./components/mobile/MobileBottomNav";

import { PullToRefresh } from "./components/mobile/PullToRefresh";
import { CookieConsent } from "./components/CookieConsent";
import NotFound from "./pages/NotFound";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { AdminRoute } from "./components/auth/AdminRoute";


// Lazy-loaded pages - reduces initial bundle by ~750KB
const BlogPage = lazy(() => import("./pages/BlogPage"));
const FinancePage = lazy(() => import("./pages/FinancePage"));
const FinancingIframePage = lazy(() => import("./pages/FinancingIframePage"));
const InsurancePage = lazy(() => import("./pages/InsurancePage"));
const HistoryPage = lazy(() => import("./pages/HistoryPage"));
const DealersPage = lazy(() => import("./pages/DealersPage"));
const YachtValuesPage = lazy(() => import("./pages/YachtValuesPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const FAQsPage = lazy(() => import("./pages/FAQsPage"));
const BoatAppraisalPage = lazy(() => import("./pages/BoatAppraisalPage"));
const NADABoatValuesPage = lazy(() => import("./pages/NADABoatValuesPage"));
const JDPowerBoatValuesPage = lazy(() => import("./pages/JDPowerBoatValuesPage"));
const KelleyBlueBookBoatsPage = lazy(() => import("./pages/KelleyBlueBookBoatsPage"));
const AIBoatValuationPage = lazy(() => import("./pages/AIBoatValuationPage"));
const YachtValueCalculatorPage = lazy(() => import("./pages/YachtValueCalculatorPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PremiumTermsPage = lazy(() => import("./pages/PremiumTermsPage"));
const DisclaimersPage = lazy(() => import("./pages/DisclaimersPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const CopyrightPage = lazy(() => import("./pages/CopyrightPage"));
const DoNotSellPage = lazy(() => import("./pages/DoNotSellPage"));
const PrivacyRequestPage = lazy(() => import("./pages/PrivacyRequestPage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const BoatWorthGuidePage = lazy(() => import("./pages/BoatWorthGuidePage"));
const YachtValueGuidePage = lazy(() => import("./pages/YachtValueGuidePage"));
const BoatDepreciationGuidePage = lazy(() => import("./pages/guides/BoatDepreciationGuidePage"));
const BoatSellingGuidePage = lazy(() => import("./pages/guides/BoatSellingGuidePage"));
const BoatBuyingGuidePage = lazy(() => import("./pages/guides/BoatBuyingGuidePage"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const WatercraftAppraisalPage = lazy(() => import("./pages/WatercraftAppraisalPage"));
const PriceMyBoatToSellPage = lazy(() => import("./pages/PriceMyBoatToSellPage"));
const BoatTradeInValuePage = lazy(() => import("./pages/BoatTradeInValuePage"));
const BoatBlueBookPage = lazy(() => import("./pages/BoatBlueBookPage"));

// New SEO landing pages
const UsedBoatValuesPage = lazy(() => import("./pages/UsedBoatValuesPage"));
const YachtValueLookupPage = lazy(() => import("./pages/YachtValueLookupPage"));
const YachtValueByVinPage = lazy(() => import("./pages/YachtValueByVinPage"));
const FairBoatPricePage = lazy(() => import("./pages/FairBoatPricePage"));
const BoatPurchasePriceEstimatorPage = lazy(() => import("./pages/BoatPurchasePriceEstimatorPage"));
const BuyerValuationPage = lazy(() => import("./pages/BuyerValuationPage"));
const WhatShouldIPayPage = lazy(() => import("./pages/WhatShouldIPayPage"));
const BoatPricingGuidePage = lazy(() => import("./pages/BoatPricingGuidePage"));

// AI/GEO Optimization pages
const MethodologyPage = lazy(() => import("./pages/MethodologyPage"));
const IntelligenceCenterPage = lazy(() => import("./pages/IntelligenceCenterPage"));
const HowBoatValuesCalculatedPage = lazy(() => import("./pages/intelligence/HowBoatValuesCalculatedPage"));
const CenterConsoleResaleValuePage = lazy(() => import("./pages/intelligence/CenterConsoleResaleValuePage"));
const YachtDepreciationPage = lazy(() => import("./pages/intelligence/YachtDepreciationPage"));
const RegionalBoatValueTrendsPage = lazy(() => import("./pages/intelligence/RegionalBoatValueTrendsPage"));
const NADAvsMarketValuationsPage = lazy(() => import("./pages/intelligence/NADAvsMarketValuationsPage"));

// Pillar Pages
const YachtAssetClassPage = lazy(() => import("./pages/pillars/YachtAssetClassPage"));
const SuperyachtMarketAnalysisPage = lazy(() => import("./pages/pillars/SuperyachtMarketAnalysisPage"));
const YachtResaleLiquidityPage = lazy(() => import("./pages/pillars/YachtResaleLiquidityPage"));
const ShipyardBacklogPricingPage = lazy(() => import("./pages/pillars/ShipyardBacklogPricingPage"));
const YachtMarketReportsPage = lazy(() => import("./pages/pillars/YachtMarketReportsPage"));
const YachtBuildersPage = lazy(() => import("./pages/pillars/YachtBuildersPage"));

// Yacht Price Intelligence Index
const YachtValuesIndexPage = lazy(() => import("./pages/yacht-values/YachtValuesIndexPage"));
const YachtBrandPage = lazy(() => import("./pages/yacht-values/YachtBrandPage"));
const YachtModelPage = lazy(() => import("./pages/yacht-values/YachtModelPage"));
const YachtModelYearPage = lazy(() => import("./pages/yacht-values/YachtModelYearPage"));
const YachtPriceTrendsPage = lazy(() => import("./pages/yacht-values/YachtPriceTrendsPage"));

// Sell My Yacht Geo Pages
const SellMyYachtIndexPage = lazy(() => import("./pages/sell-my-yacht/SellMyYachtIndexPage"));
const SellMyYachtStatePage = lazy(() => import("./pages/sell-my-yacht/SellMyYachtStatePage"));
const SellMyYachtMetroPage = lazy(() => import("./pages/sell-my-yacht/SellMyYachtMetroPage"));

// Market Intelligence Pages
const CostOfOwningAYachtPage = lazy(() => import("./pages/market-intelligence/CostOfOwningAYachtPage"));
const LuxuryYachtBrandsRankedPage = lazy(() => import("./pages/market-intelligence/LuxuryYachtBrandsRankedPage"));
const YachtValueBySizePage = lazy(() => import("./pages/market-intelligence/YachtValueBySizePage"));
const YachtSizeMarketPage = lazy(() => import("./pages/market-intelligence/YachtSizeMarketPage"));
const YachtCrewCostsPage = lazy(() => import("./pages/market-intelligence/YachtCrewCostsPage"));
const YachtDockingCostsPage = lazy(() => import("./pages/market-intelligence/YachtDockingCostsPage"));

// International Market Pages
const MediterraneanYachtMarketPage = lazy(() => import("./pages/regions/MediterraneanYachtMarketPage"));
const CaribbeanYachtMarketPage = lazy(() => import("./pages/regions/CaribbeanYachtMarketPage"));

// Geo Market Landing Pages
const UKYachtMarketPage = lazy(() => import("./pages/markets/UKYachtMarketPage"));
const AustraliaYachtMarketPage = lazy(() => import("./pages/markets/AustraliaYachtMarketPage"));
const EuropeYachtMarketPage = lazy(() => import("./pages/markets/EuropeYachtMarketPage"));
const CanadaYachtMarketPage = lazy(() => import("./pages/markets/CanadaYachtMarketPage"));
const LuxuryMetroMarketPage = lazy(() => import("./pages/markets/LuxuryMetroMarketPage"));

// Buyer Research Hub Pages
const YachtAffordabilityCalculatorPage = lazy(() => import("./pages/buyer-hub/YachtAffordabilityCalculatorPage"));
const YachtLoanCalculatorPage = lazy(() => import("./pages/buyer-hub/YachtLoanCalculatorPage"));
const CostOfOwning60ftYachtPage = lazy(() => import("./pages/buyer-hub/CostOfOwning60ftYachtPage"));
const YachtMaintenanceCostsPage = lazy(() => import("./pages/buyer-hub/YachtMaintenanceCostsPage"));

// Builder Pages
const BenettiResalePage = lazy(() => import("./pages/builders/BenettiResalePage"));
const FeadshipValuationPage = lazy(() => import("./pages/builders/FeadshipValuationPage"));
const LurssenMarketValuePage = lazy(() => import("./pages/builders/LurssenMarketValuePage"));
const AmelsResalePage = lazy(() => import("./pages/builders/AmelsResalePage"));
const HeesenResalePage = lazy(() => import("./pages/builders/HeesenResalePage"));

// Market Report Pages
const Yacht40mPlusPage = lazy(() => import("./pages/market-reports/Yacht40mPlusPage"));
const Yacht60mPlusPage = lazy(() => import("./pages/market-reports/Yacht60mPlusPage"));
const Yacht100mPlusPage = lazy(() => import("./pages/market-reports/Yacht100mPlusPage"));

// Intelligence Cluster Articles — Asset Class
const YachtDepreciatingAssetPage = lazy(() => import("./pages/intelligence/YachtDepreciatingAssetPage"));
const CanYachtsAppreciatePage = lazy(() => import("./pages/intelligence/CanYachtsAppreciatePage"));
const YachtVsPrivateJetPage = lazy(() => import("./pages/intelligence/YachtVsPrivateJetPage"));
const CharterRevenueOffsetPage = lazy(() => import("./pages/intelligence/CharterRevenueOffsetPage"));
const FamilyOfficeYachtPage = lazy(() => import("./pages/intelligence/FamilyOfficeYachtPage"));

// Intelligence Cluster Articles — Market Intelligence
const ShipyardBacklogImpactPage = lazy(() => import("./pages/intelligence/ShipyardBacklogImpactPage"));
const SuperyachtSecondaryMarketPage = lazy(() => import("./pages/intelligence/SuperyachtSecondaryMarketPage"));
const YachtBrokerageInventoryPage = lazy(() => import("./pages/intelligence/YachtBrokerageInventoryPage"));
const YachtMarketForecast2026Page = lazy(() => import("./pages/intelligence/YachtMarketForecast2026Page"));
const TimingYachtMarketPage = lazy(() => import("./pages/intelligence/TimingYachtMarketPage"));

// Intelligence Cluster Articles — Resale & Liquidity
const SuperyachtResaleValue2026Page = lazy(() => import("./pages/intelligence/SuperyachtResaleValue2026Page"));
const YachtLiquidityCompressionPage = lazy(() => import("./pages/intelligence/YachtLiquidityCompressionPage"));
const YachtExitStrategyPage = lazy(() => import("./pages/intelligence/YachtExitStrategyPage"));
const YachtMarketCyclesPage = lazy(() => import("./pages/intelligence/YachtMarketCyclesPage"));
const YachtPrivateSaleVsBrokerPage = lazy(() => import("./pages/intelligence/YachtPrivateSaleVsBrokerPage"));

// Intelligence Cluster Articles — Shipyard
const ShipyardScarcityIndexPage = lazy(() => import("./pages/intelligence/ShipyardScarcityIndexPage"));
const NewBuildVsPreOwnedPage = lazy(() => import("./pages/intelligence/NewBuildVsPreOwnedPage"));
const DeliveryDelaysImpactPage = lazy(() => import("./pages/intelligence/DeliveryDelaysImpactPage"));
const BuilderPricing2026Page = lazy(() => import("./pages/intelligence/BuilderPricing2026Page"));

// Intelligence Cluster Articles — Additional
const SuperyachtWealthPreservationPage = lazy(() => import("./pages/intelligence/SuperyachtWealthPreservationPage"));
const YachtAssetAllocationPage = lazy(() => import("./pages/intelligence/YachtAssetAllocationPage"));
const YachtLiquidityMarketPage = lazy(() => import("./pages/intelligence/YachtLiquidityMarketPage"));
const YachtInvestmentStrategyPage = lazy(() => import("./pages/intelligence/YachtInvestmentStrategyPage"));

// Boat Show Discovery Pages
const BoatShowsNearMePage = lazy(() => import("./pages/boat-shows/BoatShowsNearMePage"));
const BoatShowCalendarPage = lazy(() => import("./pages/boat-shows/BoatShowCalendarPage"));
const LargestBoatShowsPage = lazy(() => import("./pages/boat-shows/LargestBoatShowsPage"));
const BoatShowStatePage = lazy(() => import("./pages/boat-shows/BoatShowStatePage"));
const BoatShowDetailPage = lazy(() => import("./pages/boat-shows/BoatShowDetailPage"));
const BoatShowMapPage = lazy(() => import("./pages/boat-shows/BoatShowMapPage"));

// Admin pages
const AdminLayout = lazy(() => import("./layouts/AdminLayout"));
const AdminDashboardPage = lazy(() => import("./pages/admin/AdminDashboardPage"));
const AdminSecurityPage = lazy(() => import("./pages/admin/SecurityPage"));
const AdminLeadEnrichmentPage = lazy(() => import("./pages/admin/LeadEnrichmentPage"));
const AdminDataSyncPage = lazy(() => import("./pages/admin/DataSyncPage"));
const AdminDataQualityPage = lazy(() => import("./pages/admin/DataQualityPage"));
const AdminAuditLogsPage = lazy(() => import("./pages/admin/AuditLogsPage"));
const AdminWebhooksPage = lazy(() => import("./pages/admin/WebhooksPage"));
const AdminApiAccessPage = lazy(() => import("./pages/admin/ApiAccessPage"));
const AdminPricingPage = lazy(() => import("./pages/admin/PricingPage"));
const AdminPartnersPage = lazy(() => import("./pages/admin/PartnersPage"));
const AdminRevenuePage = lazy(() => import("./pages/admin/RevenuePage"));
const AdminEmailAnalyticsPage = lazy(() => import("./pages/admin/EmailAnalyticsPage"));
const AdminSEOImagesPage = lazy(() => import("./pages/admin/SEOImagesPage"));
const AdminSocialPublisherPage = lazy(() => import("./pages/admin/SocialPublisherPage"));
const AdminUserManagementPage = lazy(() => import("./pages/admin/UserManagementPage"));
 const AdminDealAlertsPage = lazy(() => import("./pages/admin/DealAlertsPage"));
const AdminValuationActivityPage = lazy(() => import("./pages/admin/ValuationActivityPage"));
const AdminToolUsageAnalyticsPage = lazy(() => import("./pages/admin/ToolUsageAnalyticsPage"));
const AdminVerificationPage = lazy(() => import("./pages/admin/VerificationPage"));
const AdminConciergeQueuePage = lazy(() => import("./pages/admin/ConciergeQueuePage"));
const AdminBoatShowsPage = lazy(() => import("./pages/admin/AdminBoatShowsPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const AccountSetupPage = lazy(() => import("./pages/AccountSetupPage"));
const MyValuationsPage = lazy(() => import("./pages/MyValuationsPage"));
const MyBoatPortfolioPage = lazy(() => import("./pages/MyBoatPortfolioPage"));
const CashOfferHubPage = lazy(() => import("./pages/CashOfferHubPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const AccountSettingsPage = lazy(() => import("./pages/AccountSettingsPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const PremiumReportPage = lazy(() => import("./pages/PremiumReportPage"));
 
 
 // Tool pages
 const NetProceedsPage = lazy(() => import("./pages/tools/NetProceedsPage"));
 const SellReadinessPage = lazy(() => import("./pages/tools/SellReadinessPage"));
 const MarketPulsePage = lazy(() => import("./pages/tools/MarketPulsePage"));
 const TradeUpPage = lazy(() => import("./pages/tools/TradeUpPage"));
 const DealAnalyzerPage = lazy(() => import("./pages/tools/DealAnalyzerPage"));
 const NegotiationGuidePage = lazy(() => import("./pages/tools/NegotiationGuidePage"));
 const CostOfOwnershipPage = lazy(() => import("./pages/tools/CostOfOwnershipPage"));
const DepreciationPage = lazy(() => import("./pages/tools/DepreciationPage"));
 const DepreciationForecastPage = lazy(() => import("./pages/tools/DepreciationForecastPage"));
const ListingGeneratorPage = lazy(() => import("./pages/tools/ListingGeneratorPage"));
const SeasonalTimingPage = lazy(() => import("./pages/tools/SeasonalTimingPage"));
const PhotoOptimizerPage = lazy(() => import("./pages/tools/PhotoOptimizerPage"));
const StagingTipsPage = lazy(() => import("./pages/tools/StagingTipsPage"));
const NotificationSettingsPage = lazy(() => import("./pages/tools/NotificationSettingsPage"));

// Lazy-loaded regional pages
const CaliforniaYachtValuePage = lazy(() => import("./pages/regions/CaliforniaYachtValuePage"));
const FloridaYachtValuePage = lazy(() => import("./pages/regions/FloridaYachtValuePage"));
const WashingtonYachtValuePage = lazy(() => import("./pages/regions/WashingtonYachtValuePage"));
const TexasYachtValuePage = lazy(() => import("./pages/regions/TexasYachtValuePage"));
const OregonYachtValuePage = lazy(() => import("./pages/regions/OregonYachtValuePage"));
const ColoradoYachtValuePage = lazy(() => import("./pages/regions/ColoradoYachtValuePage"));
const VirginiaYachtValuePage = lazy(() => import("./pages/regions/VirginiaYachtValuePage"));
const ArizonaYachtValuePage = lazy(() => import("./pages/regions/ArizonaYachtValuePage"));
const NewYorkYachtValuePage = lazy(() => import("./pages/regions/NewYorkYachtValuePage"));
const LakeOzarksYachtValuePage = lazy(() => import("./pages/regions/LakeOzarksYachtValuePage"));
const NorthCarolinaYachtValuePage = lazy(() => import("./pages/regions/NorthCarolinaYachtValuePage"));
const SouthCarolinaYachtValuePage = lazy(() => import("./pages/regions/SouthCarolinaYachtValuePage"));
const NewJerseyYachtValuePage = lazy(() => import("./pages/regions/NewJerseyYachtValuePage"));

// Lazy-loaded boat type pages
const PontoonBoatValuePage = lazy(() => import("./pages/boat-types/PontoonBoatValuePage"));
const BassBoatValuePage = lazy(() => import("./pages/boat-types/BassBoatValuePage"));
const WakeBoatValuePage = lazy(() => import("./pages/boat-types/WakeBoatValuePage"));
const YachtValuePage = lazy(() => import("./pages/boat-types/YachtValuePage"));
const SailboatValuePage = lazy(() => import("./pages/boat-types/SailboatValuePage"));
const PowerboatValuePage = lazy(() => import("./pages/boat-types/PowerboatValuePage"));
const OutboardBoatValuePage = lazy(() => import("./pages/boat-types/OutboardBoatValuePage"));
const MotorYachtValuePage = lazy(() => import("./pages/boat-types/MotorYachtValuePage"));
const SportYachtValuePage = lazy(() => import("./pages/boat-types/SportYachtValuePage"));
const FlybridgeYachtValuePage = lazy(() => import("./pages/boat-types/FlybridgeYachtValuePage"));
const ExpressCruiserValuePage = lazy(() => import("./pages/boat-types/ExpressCruiserValuePage"));
const TrawlerYachtValuePage = lazy(() => import("./pages/boat-types/TrawlerYachtValuePage"));
const PWCValuePage = lazy(() => import("./pages/boat-types/PWCValuePage"));
const CenterConsoleValuePage = lazy(() => import("./pages/boat-types/CenterConsoleValuePage"));
const BowriderValuePage = lazy(() => import("./pages/boat-types/BowriderValuePage"));
const DeckBoatValuePage = lazy(() => import("./pages/boat-types/DeckBoatValuePage"));
const CatamaranValuePage = lazy(() => import("./pages/boat-types/CatamaranValuePage"));
const FishingBoatValuePage = lazy(() => import("./pages/boat-types/FishingBoatValuePage"));
const CruiserBoatValuePage = lazy(() => import("./pages/boat-types/CruiserBoatValuePage"));
const InflatableBoatValuePage = lazy(() => import("./pages/boat-types/InflatableBoatValuePage"));
const JonBoatValuePage = lazy(() => import("./pages/boat-types/JonBoatValuePage"));
const CabinCruiserValuePage = lazy(() => import("./pages/boat-types/CabinCruiserValuePage"));
const BayBoatValuePage = lazy(() => import("./pages/boat-types/BayBoatValuePage"));
const OffshoreBoatValuePage = lazy(() => import("./pages/boat-types/OffshoreBoatValuePage"));
const AluminumBoatValuePage = lazy(() => import("./pages/boat-types/AluminumBoatValuePage"));
const HouseboatValuePage = lazy(() => import("./pages/boat-types/HouseboatValuePage"));

// Admin redirect - no longer used, kept for legacy reference
// const AdminRedirect = lazy(() => import("./components/admin/AdminRedirect"));

// Redirect /sitemap.xml to dynamic sitemap edge function (serves XML, not SPA HTML)
const SitemapRedirect = () => {
  useEffect(() => {
    window.location.replace('https://dyxfcunxsikrjewxcrqi.supabase.co/functions/v1/dynamic-sitemap');
  }, []);
  return null;
};

// Loading fallback - minimal to not affect UX
const PageLoader = () => (
  <div className="min-h-[50vh] flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const queryClient = new QueryClient();

// CSS-based page transitions for better performance (reduces main-thread work by ~500ms)
// Uses delayed location switching to prevent homepage flash during route transitions
const AnimatedRoutes = () => {
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [currentLocation, setCurrentLocation] = useState(location);
  const rafRef = useRef<{ raf1?: number; raf2?: number }>({});
  
  // When URL changes, immediately hide content, then switch routes after hide is painted
  useEffect(() => {
    if (location.pathname !== currentLocation.pathname) {
      // Immediately hide current content
      setIsVisible(false);
      
      // Small delay ensures the hide state is painted before switching routes
      const hideTimer = setTimeout(() => {
        setCurrentLocation(location);
      }, 30);
      
      return () => clearTimeout(hideTimer);
    }
  }, [location.pathname, currentLocation.pathname]);

  // Ping IndexNow on route changes (production only, debounced)
  useEffect(() => {
    const timer = setTimeout(() => pingCurrentPage(), 2000);
    return () => clearTimeout(timer);
  }, [location.pathname]);
  
  // When location updates (after hide delay), wait for Suspense to resolve then show
  useEffect(() => {
    if (!isVisible && currentLocation.pathname === location.pathname) {
      // Double RAF ensures Suspense has resolved and content is painted
      rafRef.current.raf1 = requestAnimationFrame(() => {
        rafRef.current.raf2 = requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
      return () => {
        if (rafRef.current.raf1) cancelAnimationFrame(rafRef.current.raf1);
        if (rafRef.current.raf2) cancelAnimationFrame(rafRef.current.raf2);
      };
    }
  }, [currentLocation.pathname, location.pathname, isVisible]);
  
  return (
    <div 
      className={cn("flex-1", isVisible && "animate-page-fade-in")}
      style={{ 
        opacity: isVisible ? undefined : 0,
        pointerEvents: isVisible ? 'auto' : 'none',
        contain: 'layout style'
      }}
    >
      <Suspense fallback={<PageLoader />}>
          <Routes location={currentLocation}>
                {/* Critical paths - loaded synchronously */}
                <Route path="/" element={<HomePage />} />
                <Route path="/valuation" element={<ValuationPage />} />
                
                {/* Lazy-loaded routes */}
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/account-setup" element={<AccountSetupPage />} />
                <Route path="/my-portfolio" element={<ProtectedRoute><MyBoatPortfolioPage /></ProtectedRoute>} />
                <Route path="/my-valuations" element={<Navigate to="/my-portfolio" replace />} />
                <Route path="/cash-offers" element={<ProtectedRoute><CashOfferHubPage /></ProtectedRoute>} />
                <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
                <Route path="/account-settings" element={<ProtectedRoute><AccountSettingsPage /></ProtectedRoute>} />
                 <Route path="/tools/net-proceeds" element={<ProtectedRoute><NetProceedsPage /></ProtectedRoute>} />
                 <Route path="/tools/sell-readiness" element={<ProtectedRoute><SellReadinessPage /></ProtectedRoute>} />
                 <Route path="/tools/market-pulse" element={<ProtectedRoute><MarketPulsePage /></ProtectedRoute>} />
                 <Route path="/tools/trade-up" element={<ProtectedRoute><TradeUpPage /></ProtectedRoute>} />
                 <Route path="/tools/deal-analyzer" element={<ProtectedRoute><DealAnalyzerPage /></ProtectedRoute>} />
                 <Route path="/tools/negotiation-guide" element={<ProtectedRoute><NegotiationGuidePage /></ProtectedRoute>} />
                 <Route path="/tools/cost-of-ownership" element={<ProtectedRoute><CostOfOwnershipPage /></ProtectedRoute>} />
                 <Route path="/tools/depreciation" element={<ProtectedRoute><DepreciationPage /></ProtectedRoute>} />
                  <Route path="/tools/depreciation-forecast" element={<ProtectedRoute><DepreciationForecastPage /></ProtectedRoute>} />
                 <Route path="/tools/listing-generator" element={<ProtectedRoute><ListingGeneratorPage /></ProtectedRoute>} />
                <Route path="/tools/seasonal-timing" element={<ProtectedRoute><SeasonalTimingPage /></ProtectedRoute>} />
                <Route path="/tools/photo-optimizer" element={<ProtectedRoute><PhotoOptimizerPage /></ProtectedRoute>} />
                <Route path="/tools/staging-tips" element={<ProtectedRoute><StagingTipsPage /></ProtectedRoute>} />
                <Route path="/settings/notifications" element={<ProtectedRoute><NotificationSettingsPage /></ProtectedRoute>} />
                <Route path="/upgrade" element={<Navigate to="/pricing" replace />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/report/:valuationId" element={<PremiumReportPage />} />
                <Route path="/boat-worth-guide" element={<BoatWorthGuidePage />} />
                <Route path="/boat-value-guide" element={<YachtValueGuidePage />} />
                <Route path="/guides/boat-depreciation" element={<BoatDepreciationGuidePage />} />
                <Route path="/guides/boat-selling" element={<BoatSellingGuidePage />} />
                <Route path="/guides/boat-buying" element={<BoatBuyingGuidePage />} />
                <Route path="/boat-appraisal" element={<BoatAppraisalPage />} />
                <Route path="/nada-boat-values-free" element={<NADABoatValuesPage />} />
                <Route path="/jd-power-boat-values" element={<JDPowerBoatValuesPage />} />
                <Route path="/kelley-blue-book-boats" element={<KelleyBlueBookBoatsPage />} />
                {/* Redirects for keyword variations */}
                <Route path="/kbb-boat-values" element={<Navigate to="/kelley-blue-book-boats" replace />} />
                <Route path="/kbb-boats" element={<Navigate to="/kelley-blue-book-boats" replace />} />
                <Route path="/jd-power-boats" element={<Navigate to="/jd-power-boat-values" replace />} />
                <Route path="/how-much-is-my-boat-worth" element={<Navigate to="/boat-worth-guide" replace />} />
                <Route path="/free-boat-estimator" element={<Navigate to="/" replace />} />
                <Route path="/ai-boat-valuation" element={<AIBoatValuationPage />} />
                <Route path="/boat-value-calculator" element={<YachtValueCalculatorPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:postId" element={<BlogPage />} />
                <Route path="/finance" element={<FinancePage />} />
                <Route path="/finance-application" element={<FinancingIframePage />} />
                <Route path="/resources/financing" element={<Navigate to="/finance" replace />} />
                <Route path="/resources/insurance" element={<InsurancePage />} />
                <Route path="/resources/history" element={<HistoryPage />} />
                <Route path="/resources/dealers" element={<DealersPage />} />
                <Route path="/resources/yacht-values" element={<YachtValuesPage />} />
                <Route path="/resources" element={<ResourcesPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/resources/faqs" element={<FAQsPage />} />
                {/* Legacy admin routes removed - now served by protected /admin/* */}
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/premium-terms" element={<PremiumTermsPage />} />
                <Route path="/disclaimers" element={<DisclaimersPage />} />
                <Route path="/copyright" element={<CopyrightPage />} />
                <Route path="/do-not-sell" element={<DoNotSellPage />} />
                <Route path="/unsubscribe" element={<UnsubscribePage />} />
                <Route path="/privacy-request" element={<PrivacyRequestPage />} />
                {/* New SEO Landing Pages */}
                <Route path="/watercraft-appraisal" element={<WatercraftAppraisalPage />} />
                <Route path="/price-my-boat-to-sell" element={<PriceMyBoatToSellPage />} />
                <Route path="/boat-trade-in-value" element={<BoatTradeInValuePage />} />
                <Route path="/boat-blue-book" element={<BoatBlueBookPage />} />
                <Route path="/used-boat-values" element={<UsedBoatValuesPage />} />
                <Route path="/boat-value-lookup" element={<YachtValueLookupPage />} />
                <Route path="/boat-value-by-vin" element={<YachtValueByVinPage />} />
                <Route path="/fair-boat-price" element={<FairBoatPricePage />} />
                <Route path="/boat-purchase-price-estimator" element={<BoatPurchasePriceEstimatorPage />} />
                <Route path="/buyer-valuation" element={<BuyerValuationPage />} />
                {/* AI/GEO Optimization Pages */}
                <Route path="/methodology" element={<MethodologyPage />} />
                <Route path="/intelligence" element={<IntelligenceCenterPage />} />
                <Route path="/intelligence/how-boat-values-calculated-2026" element={<HowBoatValuesCalculatedPage />} />
                <Route path="/intelligence/center-console-resale-value" element={<CenterConsoleResaleValuePage />} />
                <Route path="/intelligence/yacht-depreciation" element={<YachtDepreciationPage />} />
                <Route path="/intelligence/regional-boat-value-trends" element={<RegionalBoatValueTrendsPage />} />
                <Route path="/intelligence/nada-vs-market-valuations" element={<NADAvsMarketValuationsPage />} />
                
                {/* Pillar Pages */}
                <Route path="/yacht-asset-class" element={<YachtAssetClassPage />} />
                <Route path="/superyacht-market-analysis" element={<SuperyachtMarketAnalysisPage />} />
                <Route path="/yacht-resale-liquidity" element={<YachtResaleLiquidityPage />} />
                <Route path="/shipyard-backlog-pricing" element={<ShipyardBacklogPricingPage />} />
                <Route path="/yacht-market-reports" element={<YachtMarketReportsPage />} />
                <Route path="/yacht-builders" element={<YachtBuildersPage />} />
                
{/* Yacht Price Intelligence Index */}
                <Route path="/yacht-values" element={<YachtValuesIndexPage />} />
                <Route path="/yacht-values/:brand" element={<YachtBrandPage />} />
                <Route path="/yacht-values/:brand/:model" element={<YachtModelPage />} />
                <Route path="/yacht-values/:brand/:model/:year" element={<YachtModelYearPage />} />
                <Route path="/yacht-values/:brand/:model/price-trends" element={<YachtPriceTrendsPage />} />
                
                {/* Yacht Market Intelligence Index (parallel routes) */}
                <Route path="/yacht-market" element={<YachtValuesIndexPage />} />
                <Route path="/yacht-market/:brand" element={<YachtBrandPage />} />
                <Route path="/yacht-market/:brand/:model" element={<YachtModelPage />} />
                <Route path="/yacht-market/:brand/:model/:year" element={<YachtModelYearPage />} />
                <Route path="/yacht-market/:brand/:model/price-trends" element={<YachtPriceTrendsPage />} />
                
                {/* Size-Based Market Hubs */}
                <Route path="/yacht-market/50-foot-yachts" element={<YachtSizeMarketPage sizeKey="50-foot-yachts" />} />
                <Route path="/yacht-market/60-foot-yachts" element={<YachtSizeMarketPage sizeKey="60-foot-yachts" />} />
                <Route path="/yacht-market/80-foot-yachts" element={<YachtSizeMarketPage sizeKey="80-foot-yachts" />} />
                <Route path="/yacht-market/100-foot-yachts" element={<YachtSizeMarketPage sizeKey="100-foot-yachts" />} />
                
                {/* International Market Reports */}
                <Route path="/yacht-market/mediterranean" element={<MediterraneanYachtMarketPage />} />
                <Route path="/yacht-market/caribbean" element={<CaribbeanYachtMarketPage />} />
                
                {/* International Geo Market Pages */}
                <Route path="/markets/uk" element={<UKYachtMarketPage />} />
                <Route path="/markets/australia" element={<AustraliaYachtMarketPage />} />
                <Route path="/markets/europe" element={<EuropeYachtMarketPage />} />
                <Route path="/markets/canada" element={<CanadaYachtMarketPage />} />
                <Route path="/markets/fort-lauderdale" element={<LuxuryMetroMarketPage marketKey="fort-lauderdale" />} />
                <Route path="/markets/miami" element={<LuxuryMetroMarketPage marketKey="miami" />} />
                <Route path="/markets/palm-beach" element={<LuxuryMetroMarketPage marketKey="palm-beach" />} />
                <Route path="/markets/newport-beach" element={<LuxuryMetroMarketPage marketKey="newport-beach" />} />
                <Route path="/markets/monaco" element={<LuxuryMetroMarketPage marketKey="monaco" />} />
                <Route path="/markets/seattle" element={<LuxuryMetroMarketPage marketKey="seattle" />} />
                <Route path="/markets/mediterranean-hub" element={<LuxuryMetroMarketPage marketKey="mediterranean-hub" />} />
                
                {/* Sell My Yacht Geo Pages */}
                <Route path="/sell-my-yacht" element={<SellMyYachtIndexPage />} />
                <Route path="/sell-my-yacht/:state" element={<SellMyYachtStatePage />} />
                <Route path="/sell-my-yacht/:state/:metro" element={<SellMyYachtMetroPage />} />
                
                {/* Builder Pages */}
                <Route path="/yacht-builder/benetti" element={<BenettiResalePage />} />
                <Route path="/yacht-builder/feadship" element={<FeadshipValuationPage />} />
                <Route path="/yacht-builder/lurssen" element={<LurssenMarketValuePage />} />
                <Route path="/yacht-builder/amels" element={<AmelsResalePage />} />
                <Route path="/yacht-builder/heesen" element={<HeesenResalePage />} />
                
                {/* Size-Tier Market Reports */}
                <Route path="/yacht-market/40m-plus" element={<Yacht40mPlusPage />} />
                <Route path="/yacht-market/60m-plus" element={<Yacht60mPlusPage />} />
                <Route path="/yacht-market/100m-plus" element={<Yacht100mPlusPage />} />
                
                {/* Intelligence Cluster — Asset Class */}
                <Route path="/intelligence/yacht-as-depreciating-asset" element={<YachtDepreciatingAssetPage />} />
                <Route path="/intelligence/can-yachts-appreciate" element={<CanYachtsAppreciatePage />} />
                <Route path="/intelligence/yacht-vs-private-jet" element={<YachtVsPrivateJetPage />} />
                <Route path="/intelligence/charter-revenue-offset" element={<CharterRevenueOffsetPage />} />
                <Route path="/intelligence/family-office-yacht-structuring" element={<FamilyOfficeYachtPage />} />
                
                {/* Intelligence Cluster — Market Intelligence */}
                <Route path="/intelligence/shipyard-backlog-impact" element={<ShipyardBacklogImpactPage />} />
                <Route path="/intelligence/superyacht-secondary-market" element={<SuperyachtSecondaryMarketPage />} />
                <Route path="/intelligence/yacht-brokerage-inventory" element={<YachtBrokerageInventoryPage />} />
                <Route path="/intelligence/yacht-market-forecast-2026" element={<YachtMarketForecast2026Page />} />
                <Route path="/intelligence/timing-the-yacht-market" element={<TimingYachtMarketPage />} />
                
                {/* Intelligence Cluster — Resale & Liquidity */}
                <Route path="/intelligence/superyacht-resale-value-2026" element={<SuperyachtResaleValue2026Page />} />
                <Route path="/intelligence/yacht-liquidity-compression" element={<YachtLiquidityCompressionPage />} />
                <Route path="/intelligence/yacht-exit-strategy" element={<YachtExitStrategyPage />} />
                <Route path="/intelligence/yacht-market-cycles" element={<YachtMarketCyclesPage />} />
                <Route path="/intelligence/yacht-private-sale-vs-broker" element={<YachtPrivateSaleVsBrokerPage />} />
                
                {/* Intelligence Cluster — Shipyard */}
                <Route path="/intelligence/shipyard-scarcity-index" element={<ShipyardScarcityIndexPage />} />
                <Route path="/intelligence/new-build-vs-pre-owned" element={<NewBuildVsPreOwnedPage />} />
                <Route path="/intelligence/delivery-delays-impact" element={<DeliveryDelaysImpactPage />} />
                <Route path="/intelligence/builder-pricing-2026" element={<BuilderPricing2026Page />} />
                
                {/* Intelligence Cluster — Wealth Strategy */}
                <Route path="/intelligence/superyacht-wealth-preservation" element={<SuperyachtWealthPreservationPage />} />
                <Route path="/intelligence/superyacht-asset-allocation" element={<YachtAssetAllocationPage />} />
                <Route path="/intelligence/yacht-liquidity-market" element={<YachtLiquidityMarketPage />} />
                <Route path="/intelligence/yacht-investment-strategy" element={<YachtInvestmentStrategyPage />} />
                
                {/* Market Intelligence Hub */}
                <Route path="/cost-of-owning-a-yacht" element={<CostOfOwningAYachtPage />} />
                <Route path="/luxury-yacht-brands-ranked" element={<LuxuryYachtBrandsRankedPage />} />
                <Route path="/yacht-value-by-size" element={<YachtValueBySizePage />} />
                <Route path="/yacht-crew-costs" element={<YachtCrewCostsPage />} />
                <Route path="/yacht-docking-costs" element={<YachtDockingCostsPage />} />
                
                {/* Buyer Research Hub */}
                <Route path="/yacht-affordability-calculator" element={<YachtAffordabilityCalculatorPage />} />
                <Route path="/yacht-loan-calculator" element={<YachtLoanCalculatorPage />} />
                <Route path="/cost-of-owning-a-60-foot-yacht" element={<CostOfOwning60ftYachtPage />} />
                <Route path="/yacht-maintenance-costs" element={<YachtMaintenanceCostsPage />} />
                <Route path="/how-much-yacht-can-i-afford" element={<Navigate to="/yacht-affordability-calculator" replace />} />
                
                {/* SEO Redirects */}
                <Route path="/pwc-appraisal" element={<Navigate to="/watercraft-appraisal" replace />} />
                <Route path="/sell-my-boat" element={<Navigate to="/sell-my-yacht" replace />} />
                <Route path="/yacht-brokerage/*" element={<Navigate to="/sell-my-yacht" replace />} />
                <Route path="/trade-in-value" element={<Navigate to="/boat-trade-in-value" replace />} />
                <Route path="/blue-book-boats" element={<Navigate to="/boat-blue-book" replace />} />
                {/* Misspelling redirects */}
                <Route path="/boat-balue" element={<Navigate to="/" replace />} />
                <Route path="/boat-valie" element={<Navigate to="/" replace />} />
                <Route path="/boat-vaule" element={<Navigate to="/" replace />} />
                <Route path="/boat-calue" element={<Navigate to="/" replace />} />
                <Route path="/boat-hin-lookup" element={<Navigate to="/boat-value-by-vin" replace />} />
                <Route path="/boat-hull-number-lookup" element={<Navigate to="/boat-value-by-vin" replace />} />
                {/* Buyer keyword pages & redirects */}
                <Route path="/what-should-i-pay-for-a-boat" element={<WhatShouldIPayPage />} />
                <Route path="/boat-pricing-guide" element={<BoatPricingGuidePage />} />
                <Route path="/boat-buyers-guide" element={<Navigate to="/guides/boat-buying" replace />} />
                <Route path="/fair-price-boat" element={<Navigate to="/fair-boat-price" replace />} />
                {/* Regional Yacht Value Pages */}
                <Route path="/yacht-value/california" element={<CaliforniaYachtValuePage />} />
                <Route path="/yacht-value/florida" element={<FloridaYachtValuePage />} />
                <Route path="/yacht-value/washington" element={<WashingtonYachtValuePage />} />
                <Route path="/yacht-value/texas" element={<TexasYachtValuePage />} />
                <Route path="/yacht-value/oregon" element={<OregonYachtValuePage />} />
                <Route path="/yacht-value/colorado" element={<ColoradoYachtValuePage />} />
                <Route path="/yacht-value/virginia" element={<VirginiaYachtValuePage />} />
                <Route path="/yacht-value/arizona" element={<ArizonaYachtValuePage />} />
                <Route path="/yacht-value/new-york" element={<NewYorkYachtValuePage />} />
                <Route path="/yacht-value/lake-ozarks" element={<LakeOzarksYachtValuePage />} />
                <Route path="/yacht-value/north-carolina" element={<NorthCarolinaYachtValuePage />} />
                <Route path="/yacht-value/south-carolina" element={<SouthCarolinaYachtValuePage />} />
                <Route path="/yacht-value/new-jersey" element={<NewJerseyYachtValuePage />} />
                {/* Yacht Type Pages */}
                <Route path="/yacht-value/pontoon-boats" element={<PontoonBoatValuePage />} />
                <Route path="/yacht-value/bass-boats" element={<BassBoatValuePage />} />
                <Route path="/yacht-value/wake-boats" element={<WakeBoatValuePage />} />
                <Route path="/yacht-value/yachts" element={<YachtValuePage />} />
                <Route path="/yacht-value/sailboats" element={<SailboatValuePage />} />
                <Route path="/yacht-value/powerboats" element={<PowerboatValuePage />} />
                <Route path="/yacht-value/outboard-boats" element={<OutboardBoatValuePage />} />
                {/* Luxury Yacht Pages */}
                <Route path="/yacht-value/motor-yachts" element={<MotorYachtValuePage />} />
                <Route path="/yacht-value/sport-yachts" element={<SportYachtValuePage />} />
                <Route path="/yacht-value/flybridge-yachts" element={<FlybridgeYachtValuePage />} />
                <Route path="/yacht-value/express-cruisers" element={<ExpressCruiserValuePage />} />
                <Route path="/yacht-value/trawler-yachts" element={<TrawlerYachtValuePage />} />
                {/* Recreational Boat Pages */}
                <Route path="/yacht-value/pwc-jet-skis" element={<PWCValuePage />} />
                <Route path="/yacht-value/center-console-boats" element={<CenterConsoleValuePage />} />
                <Route path="/yacht-value/bowrider-boats" element={<BowriderValuePage />} />
                <Route path="/yacht-value/deck-boats" element={<DeckBoatValuePage />} />
                <Route path="/yacht-value/catamarans" element={<CatamaranValuePage />} />
                <Route path="/yacht-value/fishing-boats" element={<FishingBoatValuePage />} />
                <Route path="/yacht-value/cruiser-boats" element={<CruiserBoatValuePage />} />
                <Route path="/yacht-value/inflatable-boats" element={<InflatableBoatValuePage />} />
                <Route path="/yacht-value/jon-boats" element={<JonBoatValuePage />} />
                <Route path="/yacht-value/cabin-cruisers" element={<CabinCruiserValuePage />} />
                <Route path="/yacht-value/bay-boats" element={<BayBoatValuePage />} />
                <Route path="/yacht-value/offshore-boats" element={<OffshoreBoatValuePage />} />
                <Route path="/yacht-value/aluminum-boats" element={<AluminumBoatValuePage />} />
                <Route path="/yacht-value/houseboats" element={<HouseboatValuePage />} />
                {/* 301 Redirects from old /boat-value/* paths */}
                <Route path="/boat-value/california" element={<Navigate to="/yacht-value/california" replace />} />
                <Route path="/boat-value/florida" element={<Navigate to="/yacht-value/florida" replace />} />
                <Route path="/boat-value/washington" element={<Navigate to="/yacht-value/washington" replace />} />
                <Route path="/boat-value/texas" element={<Navigate to="/yacht-value/texas" replace />} />
                <Route path="/boat-value/oregon" element={<Navigate to="/yacht-value/oregon" replace />} />
                <Route path="/boat-value/colorado" element={<Navigate to="/yacht-value/colorado" replace />} />
                <Route path="/boat-value/virginia" element={<Navigate to="/yacht-value/virginia" replace />} />
                <Route path="/boat-value/arizona" element={<Navigate to="/yacht-value/arizona" replace />} />
                <Route path="/boat-value/new-york" element={<Navigate to="/yacht-value/new-york" replace />} />
                <Route path="/boat-value/lake-ozarks" element={<Navigate to="/yacht-value/lake-ozarks" replace />} />
                <Route path="/boat-value/north-carolina" element={<Navigate to="/yacht-value/north-carolina" replace />} />
                <Route path="/boat-value/south-carolina" element={<Navigate to="/yacht-value/south-carolina" replace />} />
                <Route path="/boat-value/new-jersey" element={<Navigate to="/yacht-value/new-jersey" replace />} />
                <Route path="/boat-value/pontoon-boats" element={<Navigate to="/yacht-value/pontoon-boats" replace />} />
                <Route path="/boat-value/bass-boats" element={<Navigate to="/yacht-value/bass-boats" replace />} />
                <Route path="/boat-value/wake-boats" element={<Navigate to="/yacht-value/wake-boats" replace />} />
                <Route path="/boat-value/yachts" element={<Navigate to="/yacht-value/yachts" replace />} />
                <Route path="/boat-value/sailboats" element={<Navigate to="/yacht-value/sailboats" replace />} />
                <Route path="/boat-value/powerboats" element={<Navigate to="/yacht-value/powerboats" replace />} />
                <Route path="/boat-value/outboard-boats" element={<Navigate to="/yacht-value/outboard-boats" replace />} />
                <Route path="/boat-value/motor-yachts" element={<Navigate to="/yacht-value/motor-yachts" replace />} />
                <Route path="/boat-value/sport-yachts" element={<Navigate to="/yacht-value/sport-yachts" replace />} />
                <Route path="/boat-value/flybridge-yachts" element={<Navigate to="/yacht-value/flybridge-yachts" replace />} />
                <Route path="/boat-value/express-cruisers" element={<Navigate to="/yacht-value/express-cruisers" replace />} />
                <Route path="/boat-value/trawler-yachts" element={<Navigate to="/yacht-value/trawler-yachts" replace />} />
                <Route path="/boat-value/pwc-jet-skis" element={<Navigate to="/yacht-value/pwc-jet-skis" replace />} />
                <Route path="/boat-value/center-console-boats" element={<Navigate to="/yacht-value/center-console-boats" replace />} />
                <Route path="/boat-value/bowrider-boats" element={<Navigate to="/yacht-value/bowrider-boats" replace />} />
                <Route path="/boat-value/deck-boats" element={<Navigate to="/yacht-value/deck-boats" replace />} />
                <Route path="/boat-value/catamarans" element={<Navigate to="/yacht-value/catamarans" replace />} />
                <Route path="/boat-value/fishing-boats" element={<Navigate to="/yacht-value/fishing-boats" replace />} />
                <Route path="/boat-value/cruiser-boats" element={<Navigate to="/yacht-value/cruiser-boats" replace />} />
                <Route path="/boat-value/inflatable-boats" element={<Navigate to="/yacht-value/inflatable-boats" replace />} />
                <Route path="/boat-value/jon-boats" element={<Navigate to="/yacht-value/jon-boats" replace />} />
                <Route path="/boat-value/cabin-cruisers" element={<Navigate to="/yacht-value/cabin-cruisers" replace />} />
                <Route path="/boat-value/bay-boats" element={<Navigate to="/yacht-value/bay-boats" replace />} />
                <Route path="/boat-value/offshore-boats" element={<Navigate to="/yacht-value/offshore-boats" replace />} />
                <Route path="/boat-value/aluminum-boats" element={<Navigate to="/yacht-value/aluminum-boats" replace />} />
                <Route path="/boat-value/houseboats" element={<Navigate to="/yacht-value/houseboats" replace />} />
                {/* Redirects for dead/missing routes */}
                <Route path="/boat-details" element={<Navigate to="/valuation" replace />} />
                <Route path="/Sitemap" element={<Navigate to="/sitemap.xml" replace />} />
                <Route path="/sitemap.xml" element={<SitemapRedirect />} />
                {/* Boat Show Discovery Pages */}
                <Route path="/boat-shows-near-me" element={<BoatShowsNearMePage />} />
                <Route path="/boat-shows-2026" element={<BoatShowCalendarPage />} />
                <Route path="/largest-boat-shows" element={<LargestBoatShowsPage />} />
                <Route path="/boat-shows/map" element={<BoatShowMapPage />} />
                <Route path="/boat-shows/:state" element={<BoatShowStatePage />} />
                <Route path="/boat-shows/:slug" element={<BoatShowDetailPage />} />

                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
  );
};

const AppContent = () => {
  const queryClientInstance = useQueryClient();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Initialize Core Web Vitals monitoring
    reportCoreWebVitals();
    initWebVitalsReporting();
    
    // Check if mobile using matchMedia to avoid forced reflow
    const mql = window.matchMedia('(max-width: 767px)');
    const checkMobile = () => setIsMobile(mql.matches);
    // Defer initial check with RAF to avoid forced reflow during render
    requestAnimationFrame(checkMobile);
    mql.addEventListener('change', checkMobile);
    return () => mql.removeEventListener('change', checkMobile);
  }, []);

  const handleRefresh = useCallback(async () => {
    // Invalidate all queries to refresh data
    await queryClientInstance.invalidateQueries();
    hapticFeedback('success');
  }, [queryClientInstance]);

  const content = (
    <>
      <GeoAwareAnalytics />
      
      <GeoAwareSalespanel />
      <LuxurySessionTracker />
      <ScrollToTop />
      <BreadcrumbSchema />
      <GlobalSchema />
      <MobileOptimizer />
      <PerformanceOptimizer />
      
      <MobileBottomNav />
      
      <div className="min-h-screen flex flex-col pb-16 md:pb-0">
        <Header />
        <main className="flex-1 mt-0 pt-0" style={{ contain: 'layout style' }}>
          <AnimatedRoutes />
        </main>
        <Footer />
      </div>
      <CookieConsent />
    </>
  );

  // Wrap with PullToRefresh only on mobile
  if (isMobile) {
    return (
      <PullToRefresh onRefresh={handleRefresh}>
        {content}
      </PullToRefresh>
    );
  }

  return content;
};

// AdminRoutes - no longer needed, admin is fully local now

import { AuthProvider } from '@/contexts/AuthContext';

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
          <GeoProvider>
            <GeoBlocker>
            <Routes>
              {/* Admin routes - protected with AdminRoute (requires internal_admin role) */}
              <Route path="/admin/*" element={
                <AdminRoute>
                  <Suspense fallback={<PageLoader />}>
                    <AdminLayout />
                  </Suspense>
                </AdminRoute>
              }>
                <Route index element={<Suspense fallback={<PageLoader />}><AdminDashboardPage /></Suspense>} />
                <Route path="users" element={<Suspense fallback={<PageLoader />}><AdminUserManagementPage /></Suspense>} />
                <Route path="security" element={<Suspense fallback={<PageLoader />}><AdminSecurityPage /></Suspense>} />
                <Route path="lead-enrichment" element={<Suspense fallback={<PageLoader />}><AdminLeadEnrichmentPage /></Suspense>} />
                <Route path="data-sync" element={<Suspense fallback={<PageLoader />}><AdminDataSyncPage /></Suspense>} />
                <Route path="data-quality" element={<Suspense fallback={<PageLoader />}><AdminDataQualityPage /></Suspense>} />
                <Route path="audit-logs" element={<Suspense fallback={<PageLoader />}><AdminAuditLogsPage /></Suspense>} />
                <Route path="webhooks" element={<Suspense fallback={<PageLoader />}><AdminWebhooksPage /></Suspense>} />
                <Route path="api-access" element={<Suspense fallback={<PageLoader />}><AdminApiAccessPage /></Suspense>} />
                <Route path="pricing" element={<Suspense fallback={<PageLoader />}><AdminPricingPage /></Suspense>} />
                <Route path="partners" element={<Suspense fallback={<PageLoader />}><AdminPartnersPage /></Suspense>} />
                <Route path="revenue" element={<Suspense fallback={<PageLoader />}><AdminRevenuePage /></Suspense>} />
                <Route path="email-analytics" element={<Suspense fallback={<PageLoader />}><AdminEmailAnalyticsPage /></Suspense>} />
                <Route path="seo-images" element={<Suspense fallback={<PageLoader />}><AdminSEOImagesPage /></Suspense>} />
                <Route path="social-publisher" element={<Suspense fallback={<PageLoader />}><AdminSocialPublisherPage /></Suspense>} />
                 <Route path="deal-alerts" element={<Suspense fallback={<PageLoader />}><AdminDealAlertsPage /></Suspense>} />
                <Route path="valuation-activity" element={<Suspense fallback={<PageLoader />}><AdminValuationActivityPage /></Suspense>} />
                <Route path="tool-usage" element={<Suspense fallback={<PageLoader />}><AdminToolUsageAnalyticsPage /></Suspense>} />
                <Route path="verification" element={<Suspense fallback={<PageLoader />}><AdminVerificationPage /></Suspense>} />
                <Route path="concierge-queue" element={<Suspense fallback={<PageLoader />}><AdminConciergeQueuePage /></Suspense>} />
                <Route path="boat-shows" element={<Suspense fallback={<PageLoader />}><AdminBoatShowsPage /></Suspense>} />
                {/* Legacy routes - redirect to new paths */}
                <Route path="security-monitoring" element={<Navigate to="/admin/security" replace />} />
                <Route path="capturefy-analytics" element={<Navigate to="/admin/lead-enrichment" replace />} />
                <Route path="capturefy" element={<Navigate to="/admin/lead-enrichment" replace />} />
                <Route path="salespanel-analytics" element={<Navigate to="/admin/lead-enrichment" replace />} />
                <Route path="salespanel" element={<Navigate to="/admin/lead-enrichment" replace />} />
              </Route>
              
              {/* Main app */}
              <Route path="/*" element={<AppContent />} />
            </Routes>
          </GeoBlocker>
        </GeoProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
